"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { RestaurantCard } from "@/components/RestaurantCard";
import type { RestaurantWithSlots } from "@/lib/store";
import type { Locale } from "@/lib/dictionaries";
import {
  computeAvailabilityDiff,
  type AvailabilityDiff,
} from "@/lib/availabilityDiff";

// Infer the dictionary shape from the JSON files
type Dictionary = {
  meta: { title: string; description: string };
  header: { headline: string; tagline: string };
  updatedAt: string;
  loading: string;
  error: { message: string; retry: string };
  noSlots: string;
  noSlotsBreakfast: string;
  noSlotsLunch: string;
  noSlotsDinner: string;
  available: string;
  availablePlural: string;
  booking: { sevenrooms: string; zenchef: string };
  stars: { singular: string; plural: string };
  langSwitch: string;
  stats: { nav: string };
  mealFilter: { all: string; breakfast: string; lunch: string; dinner: string };
  breakfast: string;
  lunch: string;
  dinner: string;
  availabilityDiff: { new: string; newlyAvailable: string; recentlyTaken: string };
};

const AUTO_REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour

type MealFilter = "all" | "breakfast" | "lunch" | "dinner";

function applyMealFilter(
  restaurants: RestaurantWithSlots[],
  filter: MealFilter
): RestaurantWithSlots[] {
  return restaurants
    .map((r) => ({
      ...r,
      breakfastSlots: filter === "lunch" || filter === "dinner" ? [] : r.breakfastSlots,
      lunchSlots: filter === "breakfast" || filter === "dinner" ? [] : r.lunchSlots,
      dinnerSlots: filter === "breakfast" || filter === "lunch" ? [] : r.dinnerSlots,
    }))
    .filter(
      (r) =>
        r.breakfastSlots.length > 0 ||
        r.lunchSlots.length > 0 ||
        r.dinnerSlots.length > 0
    );
}

function emptyStateMessage(filter: MealFilter, dict: Dictionary): string {
  if (filter === "breakfast") return dict.noSlotsBreakfast;
  if (filter === "lunch") return dict.noSlotsLunch;
  if (filter === "dinner") return dict.noSlotsDinner;
  return dict.noSlots;
}

function Skeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 h-40 animate-pulse"
        />
      ))}
    </div>
  );
}

function formatTime(ts: number, locale: string) {
  return new Date(ts).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Paris",
  });
}

function formatDate(date: Date, offset = 0, locale: string) {
  const d = new Date(date);
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Europe/Paris",
  });
}

// Paris calendar-date key (YYYY-MM-DD), used to detect midnight rollover so
// the availability diff baseline isn't compared across a day boundary.
function parisDateKey(d: Date): string {
  return d.toLocaleDateString("fr-CA", { timeZone: "Europe/Paris" });
}

function RestaurantSection({
  title,
  restaurants,
  count,
  emptyMessage,
  lang,
  dict,
  diff,
}: {
  title: string;
  restaurants: RestaurantWithSlots[];
  count: number;
  emptyMessage: string;
  lang: Locale;
  dict: Dictionary;
  diff: AvailabilityDiff;
}) {
  return (
    <div className="mb-10">
      <div className="flex items-baseline gap-3 pt-3 pb-4 sticky top-0 z-10 bg-neutral-950 border-b border-neutral-800">
        <h2 className="text-sm font-medium text-neutral-300 uppercase tracking-wider">
          {title}
        </h2>
        <span className="text-neutral-600 text-xs">
          {count} {count > 1 ? dict.availablePlural : dict.available}
        </span>
      </div>
      {restaurants.length === 0 ? (
        <p className="text-neutral-600 text-sm">{emptyMessage}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {restaurants.map((r) => (
            <RestaurantCard
              key={r.name}
              restaurant={r}
              lang={lang}
              dict={dict}
              diff={diff.get(r.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function HomePage({ lang, dict }: { lang: Locale; dict: Dictionary }) {
  const [today, setToday] = useState<RestaurantWithSlots[] | null>(null);
  const [tomorrow, setTomorrow] = useState<RestaurantWithSlots[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cachedAt, setCachedAt] = useState<number | null>(null);
  const [mealFilter, setMealFilter] = useState<MealFilter>("all");
  const [todayDiff, setTodayDiff] = useState<AvailabilityDiff>(() => new Map());
  const [tomorrowDiff, setTomorrowDiff] = useState<AvailabilityDiff>(() => new Map());
  // `now` is stored in state so it updates at midnight even if the page stays open
  const [now, setNow] = useState(() => new Date());

  // Baseline snapshots from the previous fetch, used to diff against the next one.
  // Refs (not state) so updating them doesn't trigger a re-render on their own.
  const prevTodayRef = useRef<RestaurantWithSlots[] | null>(null);
  const prevTomorrowRef = useRef<RestaurantWithSlots[] | null>(null);
  const prevDateKeyRef = useRef<string | null>(null);

  // Refresh `now` at the next midnight boundary (Paris date rollover)
  useEffect(() => {
    const msUntilMidnight = () => {
      const d = new Date();
      return (
        new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() -
        d.getTime()
      );
    };
    const timer = setTimeout(() => setNow(new Date()), msUntilMidnight());
    return () => clearTimeout(timer);
  }, [now]); // re-schedule each time `now` ticks over

  const dateLocale = lang === "en" ? "en-GB" : "fr-FR";

  const fetchAvailability = useCallback(async (force = false) => {
    setError(null);
    try {
      const res = force
        ? await fetch("/api/refresh", { method: "POST" })
        : await fetch("/api/availability");
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "API error");
      }
      const data = await res.json();
      const nextToday: RestaurantWithSlots[] = data.today ?? [];
      const nextTomorrow: RestaurantWithSlots[] = data.tomorrow ?? [];

      // If the Paris calendar date rolled over since the last fetch, "today"/
      // "tomorrow" no longer refer to the same days as before — reset the
      // baseline so we don't diff across the boundary.
      const dateKey = parisDateKey(new Date());
      if (prevDateKeyRef.current !== null && prevDateKeyRef.current !== dateKey) {
        prevTodayRef.current = null;
        prevTomorrowRef.current = null;
      }

      const nextTodayDiff = computeAvailabilityDiff(prevTodayRef.current, nextToday);
      const nextTomorrowDiff = computeAvailabilityDiff(
        prevTomorrowRef.current,
        nextTomorrow
      );

      setTodayDiff(nextTodayDiff);
      setTomorrowDiff(nextTomorrowDiff);
      setToday(nextToday);
      setTomorrow(nextTomorrow);
      setCachedAt(data.cachedAt ?? Date.now());
      setNow(new Date()); // sync `now` after each fetch to stay current

      prevTodayRef.current = nextToday;
      prevTomorrowRef.current = nextTomorrow;
      prevDateKeyRef.current = dateKey;
    } catch (e) {
      setError(e instanceof Error ? e.message : dict.error.message);
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
    const interval = setInterval(() => fetchAvailability(), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAvailability]);

  const filteredToday = applyMealFilter(today ?? [], mealFilter);
  const filteredTomorrow = applyMealFilter(tomorrow, mealFilter);
  const emptyMsg = emptyStateMessage(mealFilter, dict);

  const mealFilterOptions: { value: MealFilter; label: string }[] = [
    { value: "all", label: dict.mealFilter.all },
    { value: "breakfast", label: dict.mealFilter.breakfast },
    { value: "lunch", label: dict.mealFilter.lunch },
    { value: "dinner", label: dict.mealFilter.dinner },
  ];

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <header className="mb-8">
          <div className="flex items-baseline gap-3 mb-1 flex-wrap justify-between">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight">
                {dict.header.headline}
              </h1>
              <span className="text-neutral-500 text-sm capitalize">
                {formatDate(now, 0, dateLocale)}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href={`/${lang}/stats`}
                className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                {dict.stats.nav}
              </a>
              <a
                href={lang === "fr" ? "/en" : "/"}
                className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                {dict.langSwitch}
              </a>
            </div>
          </div>
          <p className="text-neutral-500 text-sm">{dict.header.tagline}</p>
          {cachedAt && (
            <p className="text-neutral-700 text-xs mt-2">
              {dict.updatedAt} {formatTime(cachedAt, dateLocale)}
            </p>
          )}
        </header>

        {/* Meal filter pill buttons */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {mealFilterOptions.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setMealFilter(value)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                mealFilter === value
                  ? "bg-white text-neutral-950 border-white font-medium"
                  : "bg-transparent text-neutral-400 border-neutral-700 hover:border-neutral-500 hover:text-neutral-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {today === null && error === null ? (
          <div>
            <p className="text-neutral-500 text-sm mb-6 animate-pulse">
              {dict.loading}
            </p>
            <Skeleton />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-neutral-400 mb-4">{dict.error.message}</p>
            <button
              onClick={() => fetchAvailability(true)}
              className="text-sm text-white border border-neutral-700 hover:border-neutral-500 rounded-lg px-4 py-2 transition-colors"
            >
              {dict.error.retry}
            </button>
          </div>
        ) : (
          <>
            <RestaurantSection
              title={formatDate(now, 0, dateLocale)}
              restaurants={filteredToday}
              count={filteredToday.length}
              emptyMessage={emptyMsg}
              lang={lang}
              dict={dict}
              diff={todayDiff}
            />
            <RestaurantSection
              title={formatDate(now, 1, dateLocale)}
              restaurants={filteredTomorrow}
              count={filteredTomorrow.length}
              emptyMessage={emptyMsg}
              lang={lang}
              dict={dict}
              diff={tomorrowDiff}
            />
          </>
        )}
      </div>
    </div>
  );
}
